/**
 * Dialog for creating a PostgreSQL backup using pg_dump.
 * Shows format selection, file path, and progress indicator during backup.
 * In Personal mode, backups are limited to databases <= 1 GB.
 */

import { useState, useEffect } from 'react';
import { Database, AlertTriangle, Terminal, Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { ProgressIndicator } from './ProgressIndicator';
import { PersonalModeBackupRestoreLimitMessage } from './PersonalModeBackupRestoreLimitMessage';
import { usePostgresBackup, usePgDumpAvailability } from '../hooks/usePostgresBackup';
import { useOperationStatus } from '../hooks/useTaskProgress';
import { useBackupRestoreStore } from '../store/backupRestoreStore';
import { useAppStore } from '../../../store/appStore';
import { useProFeature } from '../../../core/licensing/hooks/useProFeature';
import { UpgradeDialog } from '../../../core/licensing';
import { checkPersonalModeLimits, fetchDatabaseSize, type PersonalModeLimitCheck } from '../utils/personalModeLimits';
import { toast } from '../../../components/ui/toaster';
import type { PgsqlBackupFormat } from '../types/backupRestore';
import { ExternalToolHowToFixDialog } from '../../shared/external-tools/components/ExternalToolHowToFixDialog';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type PostgresBackupDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  databaseName: string;
};

const BACKUP_FORMATS: { value: PgsqlBackupFormat; label: string; description: string }[] = [
  {
    value: 'custom',
    label: 'Custom (.dump)',
    description: 'Compressed, supports parallel restore (recommended)',
  },
  {
    value: 'sql',
    label: 'Plain SQL (.sql)',
    description: 'Human-readable SQL script',
  },
];

export function PostgresBackupDialog({
  open,
  onOpenChange,
  connectionId,
  databaseName,
}: PostgresBackupDialogProps) {
  const [format, setFormat] = useState<PgsqlBackupFormat>('custom');
  const [operationId, setOperationId] = useState<string | null>(null);
  const [isCheckingSize, setIsCheckingSize] = useState(false);
  const [limitCheck, setLimitCheck] = useState<PersonalModeLimitCheck | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showHowToFix, setShowHowToFix] = useState(false);

  const operation = useOperationStatus(operationId);
  const { removeOperation } = useBackupRestoreStore();
  const pgsqlToolsSettings = useAppStore((state) => state.pgsqlToolsSettings);
  const customBinPath = pgsqlToolsSettings.customBinPath?.trim() || undefined;
  const { isAvailable, error: toolError, isChecking } = usePgDumpAvailability(customBinPath);
  const { canUse: canUseFeature } = useProFeature('backup-restore-full');

  const { backupDatabase, isBackingUp, reset } = usePostgresBackup({
    onSuccess: (result, opId) => {
      setOperationId(opId);

      if (result.success) {
        toast.success({
          title: 'Backup Complete',
          description: `Database "${databaseName}" backed up successfully`,
        });
      } else if (!result.cancelled) {
        toast.error({
          title: 'Backup Failed',
          description: result.error || 'Unknown error occurred',
        });
      }
    },
    onError: (error) => {
      toast.error({
        title: 'Backup Failed',
        description: error.message,
      });
    },
  });

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setFormat('custom');
      setOperationId(null);
      reset();
    }
  }, [open, reset]);

  // Check database size when dialog opens (Personal mode only)
  useEffect(() => {
    if (open && !canUseFeature && !limitCheck && !isCheckingSize) {
      setIsCheckingSize(true);
      fetchDatabaseSize(connectionId, databaseName, 'pgsql')
        .then((result) => {
          const check = checkPersonalModeLimits(result.success ? result.sizeInBytes ?? null : null);
          setLimitCheck(check);
        })
        .finally(() => {
          setIsCheckingSize(false);
        });
    }
  }, [open, canUseFeature, connectionId, databaseName, limitCheck, isCheckingSize]);

  // Reset limit check when dialog closes
  useEffect(() => {
    if (!open) {
      setLimitCheck(null);
    }
  }, [open]);

	  const handleStartBackup = () => {
	    backupDatabase({
	      connectionId,
	      databaseName,
	      filePath: '', // Will trigger save dialog
	      format,
	      customBinPath,
	    });
	  };

  const handleClose = () => {
    if (operationId) {
      removeOperation(operationId);
    }
    setOperationId(null);
    setLimitCheck(null);
    reset();
    onOpenChange(false);
  };

  const isComplete =
    operation?.status === 'succeeded' ||
    operation?.status === 'failed' ||
    operation?.status === 'cancelled';
  const canClose = isComplete || (!isBackingUp && !operationId);
  const hasStarted = isBackingUp || operationId !== null;
  const showingLimitMessage = limitCheck?.exceedsLimit && !canUseFeature;

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && canClose && handleClose()}>
        <DialogContent data-testid="postgres-backup-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                <Database className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <DialogTitle>Backup Database</DialogTitle>
                <DialogDescription className="mt-1">
                  Create a backup of &quot;{databaseName}&quot; using pg_dump
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {isCheckingSize ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <RandomDotmSpinner size={32} className="text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Checking database size...</p>
              </div>
            ) : showingLimitMessage && limitCheck ? (
              <PersonalModeBackupRestoreLimitMessage
                limitCheck={limitCheck}
                operationType="backup"
                onUpgrade={() => setShowUpgradeDialog(true)}
                onCancel={handleClose}
              />
            ) : (
              <>
                {/* Tool availability warning */}
                {!isChecking && !isAvailable && (
                  <div className="flex items-start gap-3 p-3 rounded-md bg-red-50 border border-red-200 dark:bg-red-950 dark:border-red-800">
                    <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                    <div className="space-y-2 text-sm text-red-800 dark:text-red-200">
                      <div className="flex items-start gap-2">
                        <Terminal className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>
                          {toolError || 'pg_dump not found. Please install PostgreSQL client tools.'}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <div className="flex items-center gap-2">
                          <Settings className="h-3 w-3 shrink-0" />
                          <span>Configure the tools path in Settings → Advanced → PostgreSQL Tools</span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => setShowHowToFix(true)}
                          data-testid="postgres-backup-how-to-fix"
                        >
                          How to fix
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {!hasStarted ? (
                  <>
                    {/* Backup format */}
                    <div className="space-y-3">
                      <Label htmlFor="backup-format">Backup format</Label>
                      <Select
                        value={format}
                        onValueChange={(v) => setFormat(v as PgsqlBackupFormat)}
                        disabled={!isAvailable}
                      >
                        <SelectTrigger id="backup-format" data-testid="backup-format-select" className="h-auto py-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="p-2">
                          {BACKUP_FORMATS.map((fmt) => (
                            <SelectItem key={fmt.value} value={fmt.value} className="py-2">
                              <div className="flex flex-col items-start text-left">
                                <span>{fmt.label}</span>
                                <span className="text-xs text-muted-foreground">{fmt.description}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Info about file location */}
                    <div className="text-sm text-muted-foreground">
                      You will be prompted to choose where to save the backup file on your computer.
                    </div>
                  </>
                ) : (
                  /* Progress indicator */
                  <ProgressIndicator
                    status={operation?.status || 'inProgress'}
                    progress={operation?.progress}
                    message={operation?.message || 'Running pg_dump...'}
                    error={operation?.error}
                    data-testid="backup-progress"
                  />
                )}
              </>
            )}
          </div>

          {!showingLimitMessage && !isCheckingSize && (
            <DialogFooter>
              {!hasStarted ? (
                <>
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    data-testid="backup-dialog-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleStartBackup}
                    disabled={!isAvailable || isChecking}
                    data-testid="backup-dialog-start"
                  >
                    Start Backup
                  </Button>
                </>
              ) : (
                <Button
                  variant={isComplete ? 'default' : 'outline'}
                  onClick={handleClose}
                  disabled={!canClose}
                  data-testid="backup-dialog-close"
                >
                  {isComplete ? 'Done' : 'Cancel'}
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
      />

      <ExternalToolHowToFixDialog
        open={showHowToFix}
        onOpenChange={setShowHowToFix}
        topic="postgres"
        missingTools={['pg_dump']}
      />
    </>
  );
}
